import { BridgeInputMessage, BridgeOutputMessage } from './bridgeBase';
import { AssessmentPackageMetadata } from '../models/AssessmentPackageMetadata';

/**
 * Type for assessment list sort direction
 */
export type ListAssessmentsSortDirection = 'asc' | 'desc';

/**
 * Type for assessment list sort field specification
 */
export type ListAssessmentsSortField = {
    field: string;
    direction: ListAssessmentsSortDirection;
};


/**
 * Enum representing the types of messages for the AssessmentPackageManager.
 */
export enum AssessmentPackageManagerMessageType {
    CheckFileExistence = 'checkFileExistence',
    FeedDownloadedFile = 'feedDownloadedFile',
    ListAssessments = 'listAssessments',
    LoadAssessment = 'loadAssessment'
}

/**
 * Enum representing the status of file existence check.
 */
export enum FileExistenceStatus {
    NotFound = 'NotFound',
    OtherVersionFound = 'OtherVersionFound',
    SameVersionFound = 'SameVersionFound'
}

/**
 * Input message for checking file existence.
 */
export class CheckFileExistenceInputMessage extends BridgeInputMessage {
    public readonly type = AssessmentPackageManagerMessageType.CheckFileExistence;
    /**
     * Creates an instance of CheckFileExistenceInputMessage.
     * @param filename - The name of the file.
     * @param filesize - The size of the file. If null, only the filename is checked.
     */
    constructor(public filename: string, public env: string, public package_type:string, public assessment_uuid: string, public valid_from: string, public valid_to: string, public school_code: number | null) {
        super();
    }
}

/**
 * Output message for checking file existence.
 */
export class CheckFileExistenceOutputMessage extends BridgeOutputMessage {
    public readonly type = AssessmentPackageManagerMessageType.CheckFileExistence;
    /**
     * Creates an instance of CheckFileExistenceOutputMessage.
     * @param status - The status of the file existence check.
     */
    constructor(public status: FileExistenceStatus) {
        super();
    }
}

/**
 * Input message for feeding a downloaded file.
 */
export class FeedDownloadedFileInputMessage extends BridgeInputMessage {
    public readonly type = AssessmentPackageManagerMessageType.FeedDownloadedFile;
    /**
     * Creates an instance of FeedDownloadedFileInputMessage.
     * @param file - The downloaded file.
     * @param metadata - The metadata associated with the file.
     */
    constructor(public file: File, public metadata: AssessmentPackageMetadata) {
        super();
    }
}

/**
 * Output message for feeding a downloaded file.
 */
export class FeedDownloadedFileOutputMessage extends BridgeOutputMessage {
    public readonly type = AssessmentPackageManagerMessageType.FeedDownloadedFile;
    constructor(public assessmentId: number) {
        super();
    }
}

/**
 * Input message for listing assessments.
 */
export class ListAssessmentsInputMessage extends BridgeInputMessage {
    public readonly type = AssessmentPackageManagerMessageType.ListAssessments;
    /**
     * Creates an instance of ListAssessmentsInputMessage.
     * @param sortFields - An array of objects specifying the fields to sort by and the direction.
     */
    constructor(public sortFields: ListAssessmentsSortField[]) {
        super();
    }
}

/**
 * Output message for listing assessments.
 */
export class ListAssessmentsOutputMessage extends BridgeOutputMessage {
    public readonly type = AssessmentPackageManagerMessageType.ListAssessments;
    /**
     * Creates an instance of ListAssessmentsOutputMessage.
     * @param assessments - An array of assessment metadata.
     */
    constructor(public assessments: AssessmentPackageMetadata[]) {
        super();
    }
}

/**
 * Input message for loading an assessment.
 */
export class LoadAssessmentInputMessage extends BridgeInputMessage {
    public readonly type = AssessmentPackageManagerMessageType.LoadAssessment;
    /**
     * Creates an instance of LoadAssessmentInputMessage.
     * @param assessmentId - The ID of the assessment to load.
     */
    constructor(public assessmentId: number) {
        super();
    }
}

/**
 * Output message for loading an assessment.
 */
export class LoadAssessmentOutputMessage extends BridgeOutputMessage {
    public readonly type = AssessmentPackageManagerMessageType.LoadAssessment;
    constructor() {
        super();
    }
}
